import { useEffect, useState } from 'react';
import { Copy, ChevronDown, ChevronRight, Trash2, Activity, Loader2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import api from '@/lib/api';
import { formatDateTime } from '@/lib/dateFormat';
import { formatLeadPhone } from '@/components/ui/PhoneInput';

interface DuplicateRow {
  id: string;
  name: string;
  // Backend returns LeadPhone[] / LeadEmail[] objects.
  phones: { isdCode?: string | null; phoneNumber?: string | null; isPrimary?: boolean }[];
  emails: { email: string; isPrimary?: boolean }[];
  status: string;
  source: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  campaignName: string | null;
  mergedAt: string | null;
  mergeSource: string | null;
  mergedByUserId: string | null;
  mergedBy: { id: string; name: string; email: string } | null;
  deletedAt: string | null;
  createdAt: string;
}

interface ApiResponse {
  master: { id: string; name: string; duplicateCount: number };
  stats: { total: number; active: number; deleted: number };
  duplicates: DuplicateRow[];
}

const SOURCE_LABEL: Record<string, string> = {
  manual_form: 'Manual entry',
  csv_import: 'CSV import',
  api: 'API',
  webhook: 'Webhook',
};

interface Props {
  leadId: string;
  /** Cached count from the master lead row — lets us skip rendering if 0
   *  without making the API call at all. */
  duplicateCount?: number;
}

export function DuplicateHistorySection({ leadId, duplicateCount }: Props) {
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'total' | 'active' | 'deleted'>('total');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const navigate = useNavigate();

  // No duplicates → show an empty state without the network call.
  const skipRender = (duplicateCount ?? 0) === 0;

  useEffect(() => {
    if (skipRender) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/leads/${leadId}/duplicates`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [leadId, skipRender]);

  if (skipRender) {
    return (
      <Card className="border-indigo-100 bg-indigo-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-indigo-900">
            <Copy className="h-4 w-4" />
            Duplicate History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 text-center py-4">
            No duplicates yet. If this lead comes in again with the same phone or email, it will be merged here.
          </p>
        </CardContent>
      </Card>
    );
  }

  const filtered = data?.duplicates.filter((d) => {
    if (filter === 'active') return !d.deletedAt;
    if (filter === 'deleted') return !!d.deletedAt;
    return true;
  }) ?? [];

  return (
    <Card className="border-indigo-100 bg-indigo-50/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2 text-indigo-900">
            <Copy className="h-4 w-4" />
            Duplicate History
            {data && (
              <span className="text-xs font-normal text-indigo-600">
                — This lead came in {data.stats.total} time{data.stats.total === 1 ? '' : 's'}
              </span>
            )}
          </CardTitle>

          {/* Clickable count badges. Click filters the list below. */}
          {data && (
            <div className="flex gap-1.5">
              {(['total', 'active', 'deleted'] as const).map((key) => {
                const value = data.stats[key];
                const isActive = filter === key;
                const colorMap = {
                  total: isActive
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50',
                  active: isActive
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50',
                  deleted: isActive
                    ? 'bg-slate-600 text-white border-slate-600'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50',
                };
                return (
                  <button
                    key={key}
                    onClick={() => setFilter(key)}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors capitalize ${colorMap[key]}`}
                  >
                    {key}: {value}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />
          </div>
        )}

        {!loading && data && filtered.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-4">
            No {filter === 'total' ? '' : filter} duplicates.
          </p>
        )}

        {!loading && filtered.length > 0 && (
          <div className="space-y-2">
            {filtered.map((d) => {
              const phones = Array.isArray(d.phones) ? d.phones : [];
              const emails = Array.isArray(d.emails) ? d.emails : [];
              const isExpanded = expandedRow === d.id;
              return (
                <div
                  key={d.id}
                  className={`rounded-lg border bg-white ${d.deletedAt ? 'border-slate-200 opacity-70' : 'border-indigo-100'}`}
                >
                  <button
                    onClick={() => setExpandedRow(isExpanded ? null : d.id)}
                    className="w-full flex items-start gap-3 p-3 text-left hover:bg-slate-50 rounded-lg"
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-800">
                          {d.mergeSource ? SOURCE_LABEL[d.mergeSource] || d.mergeSource : 'Duplicate'}
                        </span>
                        {d.deletedAt && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                            <Trash2 className="h-2.5 w-2.5" /> Deleted
                          </span>
                        )}
                        {d.source && (
                          <span className="text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                            {d.source}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {formatDateTime(d.mergedAt || d.createdAt)}
                        {d.mergedBy && ` · by ${d.mergedBy.name}`}
                      </p>
                      <div className="mt-1.5 flex items-center gap-3 flex-wrap text-xs text-slate-600">
                        {phones.length > 0 && (
                          <span>📞 {formatLeadPhone(phones[0])}{phones.length > 1 && ` +${phones.length - 1}`}</span>
                        )}
                        {emails.length > 0 && (
                          <span>✉️ {emails[0].email}{emails.length > 1 && ` +${emails.length - 1}`}</span>
                        )}
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-3 pb-3 pt-0 ml-7 space-y-2 text-xs text-slate-600 border-t border-slate-100">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mt-2 [&>div]:min-w-0 [&>div]:break-words">
                        {phones.map((p, i) => (
                          <div key={`p-${i}`}>
                            <span className="text-slate-400">Phone {i + 1}:</span>{' '}
                            <span className="font-medium">{formatLeadPhone(p)}</span>
                          </div>
                        ))}
                        {emails.map((e, i) => (
                          <div key={`e-${i}`}>
                            <span className="text-slate-400">Email {i + 1}:</span>{' '}
                            <span className="font-medium">{e.email}</span>
                          </div>
                        ))}
                        {d.utmSource && (
                          <div><span className="text-slate-400">utm_source:</span> <span className="font-mono">{d.utmSource}</span></div>
                        )}
                        {d.utmMedium && (
                          <div><span className="text-slate-400">utm_medium:</span> <span className="font-mono">{d.utmMedium}</span></div>
                        )}
                        {d.utmCampaign && (
                          <div><span className="text-slate-400">utm_campaign:</span> <span className="font-mono">{d.utmCampaign}</span></div>
                        )}
                        {d.campaignName && (
                          <div><span className="text-slate-400">campaign:</span> <span>{d.campaignName}</span></div>
                        )}
                      </div>

                      <div className="pt-2 flex items-center gap-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/leads/${d.id}`); }}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700"
                        >
                          <ExternalLink className="h-3 w-3" /> View duplicate record
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
